import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Users, Search, UserX, UserCheck, X, AlertCircle, CheckCircle } from 'lucide-react';
import {
  getCustomers,
  blockCustomer,
  unblockCustomer,
  filterCustomers,
  clearError,
  clearSuccess
} from '../redux/CustomerSlice';

const Customers = () => {
  const dispatch = useDispatch();
  const { filteredCustomers, loading, error, success, message } = useSelector(state => state.customers);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Fetch customers on component mount
  useEffect(() => {
    dispatch(getCustomers());
  }, [dispatch]);

  // Handle filtering
  useEffect(() => {
    dispatch(filterCustomers({ searchTerm, status: filterStatus }));
  }, [searchTerm, filterStatus, dispatch]);

  // Auto-dismiss success/error messages after 5 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error, dispatch]);

  const handleBlockClick = (customer) => {
    setSelectedCustomer(customer);
    setActionType('block');
    setShowConfirmModal(true);
  };

  const handleUnblockClick = (customer) => {
    setSelectedCustomer(customer);
    setActionType('unblock');
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (actionType === 'block') {
      await dispatch(blockCustomer(selectedCustomer?.id));
    } else if (actionType === 'unblock') {
      await dispatch(unblockCustomer(selectedCustomer?.id));
    }
    setShowConfirmModal(false);
    setSelectedCustomer(null);
    setActionType(null);
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
    setSelectedCustomer(null);
    setActionType(null);
  };

  const getStatusColor = (isBlocked) => {
    return isBlocked 
      ? 'bg-red-100 text-red-800' 
      : 'bg-green-100 text-green-800';
  };

  return (
    <div className="p-6">
      {/* Success/Error Notification */}
      {(success || error) && (
        <div className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
          success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
        }`}>
          <div className="flex items-center space-x-3">
            {success ? (
              <CheckCircle className="text-green-600" size={20} />
            ) : (
              <AlertCircle className="text-red-600" size={20} />
            )}
            <span className={success ? 'text-green-800' : 'text-red-800'}>
              {success ? message : error}
            </span>
          </div>
          <button
            onClick={() => {
              dispatch(clearSuccess());
              dispatch(clearError());
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
          <p className="text-gray-500 text-sm">Manage your customers</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-6 border border-gray-100 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent w-full"
              />
            </div>
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          >
            <option value="all">All Customers</option>
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            All Customers ({filteredCustomers?.length || 0})
          </h3>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600 mx-auto"></div>
              <p className="text-gray-500 mt-4">Loading customers...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Customer</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Email</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Phone</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Total Orders</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Status</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Joined</th>
                    <th className="text-left text-sm font-medium text-gray-500 pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers?.map((customer) => (
                    <tr key={customer?.id } className="border-b border-gray-50">
                      <td className="py-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                            <span className="text-amber-600 font-medium text-sm">
                              {customer?.firstName?.[0]}{customer?.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {customer?.name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 text-sm text-gray-600">{customer?.email}</td>
                      <td className="py-4 text-sm text-gray-600">{customer?.phone || 'N/A'}</td>
                      <td className="py-4 text-sm text-gray-600">{customer?.totalOrders || 0}</td>
                      <td className="py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(customer?.isBlocked)}`}>
                          {customer?.isBlocked ? 'Blocked' : 'Active'}
                        </span>
                      </td>
                      <td className="py-4 text-sm text-gray-600">
                        {customer?.created_at ? new Date(customer?.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="py-4">
                        {customer?.isBlocked ? (
                          <button
                            onClick={() => handleUnblockClick(customer)}
                            className="flex items-center space-x-1 px-3 py-1 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                            title="Unblock Customer"
                          >
                            <UserCheck size={16} />
                            <span className="text-sm">Unblock</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBlockClick(customer)}
                            className="flex items-center space-x-1 px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Block Customer"
                          >
                            <UserX size={16} />
                            <span className="text-sm">Block</span>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredCustomers?.length === 0 && !loading && (
                <div className="text-center py-12">
                  <Users size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No customers found</h3>
                  <p className="text-gray-500">Try adjusting your search or filters</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6">
            <div className="flex items-center space-x-3 mb-4">
              {actionType === 'block' ? (
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <UserX className="text-red-600" size={24} />
                </div>
              ) : (
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <UserCheck className="text-green-600" size={24} />
                </div>
              )}
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {actionType === 'block' ? 'Block Customer' : 'Unblock Customer'}
                </h3>
              </div>
            </div>

            <p className="text-gray-600 mb-6">
              {actionType === 'block' 
                ? `Are you sure you want to block ${selectedCustomer?.firstName} ${selectedCustomer?.lastName}? They will not be able to place orders.`
                : `Are you sure you want to unblock ${selectedCustomer?.firstName} ${selectedCustomer?.lastName}? They will be able to place orders again.`
              }
            </p>

            <div className="flex space-x-3">
              <button
                onClick={handleCancelAction}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAction}
                className={`flex-1 px-4 py-2 rounded-lg transition-colors ${
                  actionType === 'block'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {actionType === 'block' ? 'Block' : 'Unblock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customers;